import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * TreeSyncContext for managing tree refresh triggers
 * Used to notify tree components when requirements are modified
 */
const TreeSyncContext = createContext({
  refreshTrigger: 0,
  triggerRefresh: () => {},
  lastModifiedRequirement: null,
  setLastModifiedRequirement: () => {}
});

/**
 * TreeSyncProvider component
 */
export const TreeSyncProvider = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lastModifiedRequirement, setLastModifiedRequirement] = useState(null);

  const triggerRefresh = useCallback((requirementId = null) => {
    setRefreshTrigger(prev => prev + 1);
    if (requirementId) {
      setLastModifiedRequirement(requirementId);
    }
  }, []);

  // useMemo (#943, react/jsx-no-constructed-context-values): a fresh object every
  // render re-renders every tree consumer, which is the opposite of what a
  // refresh-trigger context is for.
  const value = useMemo(() => ({
    refreshTrigger,
    triggerRefresh,
    lastModifiedRequirement,
    setLastModifiedRequirement
  }), [refreshTrigger, triggerRefresh, lastModifiedRequirement]);

  return (
    <TreeSyncContext.Provider value={value}>
      {children}
    </TreeSyncContext.Provider>
  );
};

TreeSyncProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to use tree sync context
 */
export const useTreeSync = () => {
  const context = useContext(TreeSyncContext);
  if (!context) {
    throw new Error('useTreeSync must be used within TreeSyncProvider');
  }
  return context;
};

export default TreeSyncContext;